// Copyright 2020 TOKU Systems Inc.

import { VictoryStyleObject } from 'victory-core';
import _ from 'lodash';
import {
  TemporalResolution,
  TimeSeries,
  SummaryPoint,
  Signal,
  Measurement,
} from '@equestria/spike';
import { DomainTuple } from 'victory-core';
import { List, Map } from 'immutable';
import moment from 'moment-timezone';

interface SignalPlotStyles {
  line: VictoryStyleObject;
  area: VictoryStyleObject;
}

export interface SignalPlot {
  // The signal to plot
  signal: Signal;
  rawResolution: TemporalResolution;
  styles?: Partial<SignalPlotStyles>;
  visible: boolean;
  format: (value: number) => string;
}

export interface SignalCursorValue {
  timestamp: moment.Moment;
  signalId: string;
  mean: Measurement;
  min: Measurement;
  max: Measurement;
  variance: Measurement;
  count: number;
}

export const normalizeSignalPlot = (
  s: SignalPlot,
  styles: Partial<SignalPlotStyles> = {}
): Readonly<SignalPlot> => {
  return Object.assign({}, s, {
    styles: {
      line: Object.assign(
        {},
        styles?.line ?? {},
        { stroke: s.styles?.line?.color },
        _.get(s, ['styles', 'line'], {})
      ),
      area: Object.assign(
        {},
        _.get(s, ['styles', 'line'], {}),
        styles?.area ?? {},
        {
          fill: s.styles?.area?.fill ?? s.styles?.line?.color,
        },
        _.get(s, ['styles', 'area'], {})
      ),
    },
  });
};

export function mergeRanges(
  ranges: Map<string, DomainTuple>
): Map<string, DomainTuple> {
  const mergedEntries = List(ranges.entries()).reduce<
    List<[string, DomainTuple]>
  >((reduction, r, i) => {
    // We only want to compare to previous ranges
    const previousRanges = reduction.slice(0, i);
    const connectedRange = previousRanges.reduce<DomainTuple>(
      (largestRange, pr) => {
        if (
          pr[1][0].valueOf() <= largestRange[1].valueOf() &&
          pr[1][1].valueOf() >= largestRange[0].valueOf()
        ) {
          return [
            Math.min(pr[1][0].valueOf(), largestRange[0].valueOf()),
            Math.max(pr[1][1].valueOf(), largestRange[1].valueOf()),
          ];
        } else {
          return largestRange;
        }
      },
      r[1]
    );

    const mergedPreviousRanges: List<[string, DomainTuple]> =
      previousRanges.map((r2) => {
        if (
          r2[1][0].valueOf() <= connectedRange[1].valueOf() &&
          r2[1][1].valueOf() >= connectedRange[0].valueOf()
        ) {
          return [r2[0], connectedRange];
        } else {
          return r2;
        }
      });

    return mergedPreviousRanges
      .push([r[0], connectedRange])
      .concat(reduction.slice(i + 1));
  }, List());

  return mergedEntries.reduce(
    (reduction, entry) => reduction.set(entry[0], entry[1]),
    Map()
  );
}

export function makeSignalRanges(
  summaries: Map<string, TimeSeries<SummaryPoint>>
): Map<string, DomainTuple> {
  return summaries.map((timeSeries) => {
    const yStats = _.reduce(
      timeSeries.points,
      (stats, p) => {
        stats.min = Math.min(stats.min, p.min);
        stats.max = Math.max(stats.max, p.max);
        return stats;
      },
      { min: Number.MAX_VALUE, max: Number.MIN_VALUE }
    );

    return [yStats.min, yStats.max];
  });
}

/**
 * Denormalize a value
 * @param range Denormalized range
 * @param y normalized value [0, 1]
 */
export function denormalizeValue(range: DomainTuple, y: number): number {
  const b = range[0].valueOf();
  const m = range[1].valueOf() - range[0].valueOf();
  return m * y + b;
}

/**
 * Normalize summaries to [0, 1]
 * @param summaries summaries to normalize
 * @param ranges denormalized ranges
 */
export function normalizeSummaries(
  summaries: Map<string, TimeSeries<SummaryPoint>>,
  ranges: Map<string, DomainTuple>
): Map<string, TimeSeries<SummaryPoint>> {
  return summaries.map((timeSeries, key) => {
    // Calculate m and b.
    // b = range[0]
    // m = range[1] - range[0]
    const signalRange: DomainTuple = ranges.get(key) ?? [0, 1];
    const b = signalRange[0].valueOf();
    const m = signalRange[1].valueOf() - signalRange[0].valueOf();

    return {
      points: timeSeries.points.map((p) => ({
        t: p.t,
        mean: (p.mean - b) / m,
        min: (p.min - b) / m,
        max: (p.max - b) / m,
        variance: p.variance / m,
        count: p.count,
      })),
      measuringUnit: timeSeries.measuringUnit,
    };
  });
}

export function denormalizeSummaries(
  summaries: Map<string, TimeSeries<SummaryPoint>>,
  ranges: Map<string, DomainTuple>
): Map<string, TimeSeries<SummaryPoint>> {
  return summaries.map((timeSeries, key) => {
    // Calculate m and b.
    // b = range[0]
    // m = range[1] - range[0]
    const signalRange: DomainTuple = ranges.get(key) ?? [0, 1];
    const b = signalRange[0].valueOf();
    const m = signalRange[1].valueOf() - signalRange[0].valueOf();

    return {
      points: timeSeries.points.map((p) => ({
        t: p.t,
        mean: m * p.mean + b,
        min: m * p.min + b,
        max: m * p.max + b,
        variance: p.variance * m,
        count: p.count,
      })),
      measuringUnit: timeSeries.measuringUnit,
    };
  });
}

export function temporalResolutionForSamplingFrequency(
  samplingFrequency: number
): TemporalResolution {
  if (samplingFrequency <= 1 / 2700) {
    return TemporalResolution.Hour;
  } else if (samplingFrequency <= 1 / 45) {
    return TemporalResolution.Minute;
  } else if (samplingFrequency < 2) {
    return TemporalResolution.Second;
  } else {
    throw new Error(
      `Sampling frequency ${samplingFrequency} does not have an associated temporal resolution.`
    );
  }
}

export const colorPalette: List<string> = List([
  '#0000CF',
  '#CF00CF',
  '#CFCF00',
  '#008B02',
  '#006B76',
  '#1273DE',
  '#004DCF',
  '#5300EB',
  '#EB9694',
  '#FAD0C3',
  '#FEF3BD',
  '#C1E1C5',
  '#BEDADC',
  '#C4DEF6',
  '#BED3F3',
  '#D4C4FB',
]);

const weekMs = moment.duration('P1W').asMilliseconds();

// Clamp domain to 1 week
export function clampDomain(x: DomainTuple): DomainTuple {
  const selectedDuration = x[1].valueOf() - x[0].valueOf();
  if (selectedDuration > weekMs) {
    const boundedSelectedStart =
      x[0].valueOf() + (selectedDuration - weekMs) / 2;
    x[0] = boundedSelectedStart;
    x[1] = boundedSelectedStart + weekMs;
  }
  return x;
}
